/**
 * Page-local URL params for StaffRolesPage's two list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: the status and role filters (both applied client-side
 * to the merged users list). Eighth adoption of the pattern #78 established (after #97-#102).
 *
 * Deliberately OUT of scope: the edit dialog (editingUser/editRoles/editReviewerId), the bulk-role
 * dialog (bulkRoles/bulkMode/bulkReviewerId/bulkDialogOpen), the admin-removal confirmation
 * (confirmRemoveAdmin), row selection (selectedIds), and `pageItems` -- a value DataTable's own
 * pagination callback sets, not something a user configures, so it isn't view state either.
 *
 * Conventions (mirroring the other hooks in this sweep):
 * - Each param is present only when it differs from the page's own default (status: 'active',
 *   role: 'all'); an unrecognised value falls back to that default.
 * - Mutations replace history (never push).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { StaffRoleType } from '../types';

type StatusFilter = 'active' | 'inactive' | 'all';
type RoleFilter = StaffRoleType | 'all' | 'none';

const VALID_STATUSES: readonly StatusFilter[] = ['active', 'inactive', 'all'];
const VALID_ROLES: readonly RoleFilter[] = ['admin', 'preparer', 'reviewer', 'reception', 'staff', 'all', 'none'];

export interface StaffRolesScopeParams {
  statusFilter: StatusFilter;
  roleFilter: RoleFilter;
  setStatusFilter: (status: StatusFilter) => void;
  setRoleFilter: (role: RoleFilter) => void;
}

export function useStaffRolesScopeParams(): StaffRolesScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = useMemo<StatusFilter>(() => {
    const raw = searchParams.get('status');
    return raw && (VALID_STATUSES as string[]).includes(raw) ? (raw as StatusFilter) : 'active';
  }, [searchParams]);

  const roleFilter = useMemo<RoleFilter>(() => {
    const raw = searchParams.get('role');
    return raw && (VALID_ROLES as string[]).includes(raw) ? (raw as RoleFilter) : 'all';
  }, [searchParams]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatusFilter = useCallback(
    (next: StatusFilter) => {
      update((params) => {
        if (next === 'active') params.delete('status');
        else params.set('status', next);
      });
    },
    [update]
  );

  const setRoleFilter = useCallback(
    (next: RoleFilter) => {
      update((params) => {
        if (next === 'all') params.delete('role');
        else params.set('role', next);
      });
    },
    [update]
  );

  return { statusFilter, roleFilter, setStatusFilter, setRoleFilter };
}
